import moment from "moment";
import { useMemo } from "react";

type UseDatesReturn = {
  formattedDate: string;
  weekLabel: "Weekday" | "Weekend" | "";
  numberOfDays: number;
  numberOfDaysLabel?: string;
};

const useDates = (dates: string[]): UseDatesReturn => {
  return useMemo(() => {
    if (!Array.isArray(dates) || dates.length === 0) {
      return {
        formattedDate: "",
        weekLabel: "",
        numberOfDays: 0,
        numberOfDaysLabel: "",
      };
    }

    type GroupedDates = {
      [key: string]: number[];
    };

    const groups: GroupedDates = {};
    const yearsSet = new Set<string>();
    let firstValidDate: moment.Moment | null = null;
    let validCount = 0;

    dates.forEach((dateStr) => {
      const date = moment(dateStr, ["D-M-YYYY", "YYYY-MM-DD"], true); // strict mode
      if (!date.isValid()) return;
      if (!firstValidDate) firstValidDate = date;

      const day = date.date();
      const month = date.format("MMM");
      const year = date.year().toString();
      const key = `${month}-${year}`;

      yearsSet.add(year);
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(day);

      validCount++;
    });

    const allSameYear = yearsSet.size === 1;
    const formatted = Object.entries(groups)
      .map(([key, days]) => {
        const [month, year] = key.split("-");
        const sortedDays = days.sort((a, b) => a - b).join(", ");
        return `${sortedDays} ${month}${allSameYear ? "" : ` ${year}`}`;
      })
      .join(" - ");

    const weekLabel: "Weekday" | "Weekend" | "" = firstValidDate
      ? //@ts-ignore
        firstValidDate.day() === 0 || firstValidDate.day() === 6
        ? "Weekend"
        : "Weekday"
      : "";

    const formattedDate = allSameYear
      ? `${formatted} ${Array.from(yearsSet)[0]}`
      : formatted;
    const numberOfDaysLabel = validCount > 1 ? "Days" : "Day";
    return {
      formattedDate,
      numberOfDays: validCount,
      weekLabel: weekLabel,
      numberOfDaysLabel: validCount + " " + numberOfDaysLabel,
    };
  }, [dates]);
};

export default useDates;
